import {Injectable} from '@angular/core';
import {Observable, zip} from 'rxjs';
import {Cart, CartStatus} from './Cart';
import {map} from 'rxjs/operators';
import {CartService, LineItem} from './cart.service';
import {DownloadDataService, UserDownloadInfo, DownloadType} from '@uofu-uss-aux/software-catalog-ng-core';
import {flatten, keyBy, uniqBy, values} from 'lodash-es';

export interface ItemDownloadInfo {
  productOfferingId: number;
  downloads: ReadonlyArray<UserDownloadInfo>;
  licenses: ReadonlyArray<UserDownloadInfo>;
}

export interface CartDownloadInfo {
  cart: Cart;
  items: ReadonlyArray<ItemDownloadInfo>;
}

type ItemDownloadInfoById = { [key: number]: ItemDownloadInfo };
type CartDownloadInfoById = { [key: number]: CartDownloadInfo };


export interface DownloadInfoWithLineItem {
  downloadInfo: ItemDownloadInfo;
  lineItem: LineItem;
}

export interface CartDownloadInfoWithLineItems {
  cart: Cart;
  downloadInfoItems: ReadonlyArray<DownloadInfoWithLineItem>;
}

@Injectable({
  providedIn: 'root'
})
export class DownloadService {

  constructor(private readonly downloadDataService: DownloadDataService,
              private readonly cartService: CartService) {
  }

  fetchAvailableByUnid(unid: string): Observable<ReadonlyArray<CartDownloadInfo>> {
    return zip(this.cartService.fetchCarts([CartStatus.Purchased], unid), this.downloadDataService.fetchAvailableByUnid(unid))
      .pipe(map(([carts, downloads]) => this.mapToCartDownloadInfo(carts, downloads)));
  }

  fetchAvailableByCart(cartId: number): Observable<readonly ItemDownloadInfo[]> {
    return this.downloadDataService.fetchAvailableByCart(cartId)
      .pipe(map(downloads => values(this.mapToItemDownloadInfo(downloads))));
  }

  combineCartDownloadsWithLineItem(items: readonly LineItem[],
                                   downloads: CartDownloadInfo): CartDownloadInfoWithLineItems {
    const infoWithLineItems = this.combineDownloadsWithLineItem(items, downloads.items);
    return this.generateCartDownloadInfoWithLineItems(downloads.cart, infoWithLineItems);
  }

  combineDownloadsWithLineItem(lineItems: readonly LineItem[], downloads: readonly ItemDownloadInfo[]):
    ReadonlyArray<DownloadInfoWithLineItem> {
    const lineItemsByOfferingId = keyBy(lineItems, (item) => item.offering.id);
    return downloads.map(downloadInfo => {
      return {
        downloadInfo,
        lineItem: lineItemsByOfferingId[downloadInfo.productOfferingId]
      };
    });
  }

  reduceToUniqueList(cartDownloads: ReadonlyArray<CartDownloadInfoWithLineItems>): ReadonlyArray<DownloadInfoWithLineItem> {
    const downloadItems: DownloadInfoWithLineItem[] = flatten(cartDownloads.map(cd => cd.downloadInfoItems));
    return uniqBy(downloadItems, ((di: DownloadInfoWithLineItem) => di.lineItem.offering.id));
  }

  private generateCartDownloadInfoWithLineItems(cart: Cart, downloadInfoItems: readonly DownloadInfoWithLineItem[]):
    CartDownloadInfoWithLineItems {
    return {
      cart,
      downloadInfoItems,
    };
  }

  private mapToCartDownloadInfo(carts: readonly Cart[], downloads: readonly UserDownloadInfo[]): ReadonlyArray<CartDownloadInfo> {
    const itemDownloadInfo = this.mapToItemDownloadInfo(downloads);
    const mapped: CartDownloadInfoById = {};

    carts.forEach(current => {
      const itemsForCart = this.filterByCart(current, itemDownloadInfo);
      if (itemsForCart.length) {
        const found = this.findOrGenerateCartDownloadInfo(current, mapped);
        found.items = found.items.concat(itemsForCart);
      }
    });

    return values(mapped);
  }

  private mapToItemDownloadInfo(downloads: readonly UserDownloadInfo[]): ItemDownloadInfoById {
    const mapped: ItemDownloadInfoById = {};

    downloads.forEach(current => {
      const found = this.findOrGenerateItemDownloadInfo(current.productOfferingId, mapped);
      const key: keyof ItemDownloadInfo = current.type === DownloadType.LICENSE ? 'licenses' : 'downloads';
      found[key] = found[key].concat(current);
    });

    return mapped;
  }

  private filterByCart(cart: Cart, downloadInfo: ItemDownloadInfoById): ReadonlyArray<ItemDownloadInfo> {
    const found = [];

    cart.items.forEach(item => {
      const thingToPush = downloadInfo[item.productOfferingId];
      if (thingToPush) {
        found.push(thingToPush);
      }
    });

    return found;
  }

  private findOrGenerateCartDownloadInfo(cart: Cart, mapped: CartDownloadInfoById): CartDownloadInfo {
    const found = mapped[cart.id];
    return found || (mapped[cart.id] = this.generateBlankCartDownloadInfo(cart));
  }

  private findOrGenerateItemDownloadInfo(productOfferingId: number, mapped: ItemDownloadInfoById): ItemDownloadInfo {
    const found = mapped[productOfferingId];
    return found || (mapped[productOfferingId] = this.generateBlankItemDownloadInfo(productOfferingId));
  }

  private generateBlankCartDownloadInfo(cart: Cart): CartDownloadInfo {
    return {
      cart,
      items: [],
    };
  }

  private generateBlankItemDownloadInfo(productOfferingId: number): ItemDownloadInfo {
    return {
      productOfferingId,
      downloads: [],
      licenses: [],
    };
  }
}
